import classNames from 'classnames'
import { t } from 'i18next'
import { FilterMatchMode } from 'primereact/api'
import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { ProgressBar } from 'primereact/progressbar'
import { Tag, TagSeverityType } from 'primereact/tag'
import { Toast } from 'primereact/toast'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  API_TRIAL_PERIOD_REPORT,
  API_TRIAL_PERIOD_REPORT_DOWNLOAD,
  API_TRIAL_PERIOD_REPORT_UPLOAD,
  MAX_FILE_SIZE,
} from '../../../../../constants'
import { RequestMethod, Status } from '../../../../../model/enums/enums'
import { Apprentice, Person } from '../../../../../model/person.model'
import { SignedUrlResponse } from '../../../../../model/signedUrlResponse.model'
import { TrialPeriodReport } from '../../../../../model/trialPeriodReport.model'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import {
  addTrialPeriodReport,
  removeTrialPeriodReport,
  updateTrialPeriodReport,
} from '../../../../../redux/official-document/trial-period-report/trialPeriodReportActions'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { handleFileUpload } from '../../../../../services/api/handleFileUpload'
import { loadAllTrialPeriodReports } from '../../../../../services/api/mokaiApiService'
import {
  findApprenticeById,
  findPersonById,
  findTrialReportById,
} from '../../../../../util/arraySearch'
import { getFormattedDateWithoutHours } from '../../../../../util/formatting'
import { UniqueKeyGenerator } from '../../../../../util/uniqueKeyGenerator'
import { AvatarWithName } from '../../../../util/AvatarWithName'
import {
  showCheckMarkToast,
  showErrorToast,
} from '../../../../util/checkmark-toast/CheckmarkToast'
import { actionBodyWithEditAndDelete } from '../../../../util/data-table/cell-body/ActionBody'
import { InactiveRowStyleByObjectAndPerson } from '../../../../util/data-table/row-class/InactiveRow'
import { DeleteConfirmationDialog } from '../../../../util/dialog/DeleteConfirmationDialog'
import { EmployeeDataTableFilter } from '../../../../util/employee-filter/EmployeeDataTableFilter'
import { MokaiUpload } from '../../../../util/form/MokaiUpload'
import { LoadingSpinner } from '../../../../util/loading-spinner/LoadingSpinner'

export const apprenticeCellBody = (
  rowData: TrialPeriodReport,
  apprentices: Apprentice[]
): JSX.Element => {
  if (rowData?.id_apprentice === undefined) {
    return getUnknownCell()
  }

  const apprentice = findApprenticeById(rowData.id_apprentice, apprentices)
  if (!apprentice) {
    return getUnknownCell()
  }

  return <AvatarWithName person={apprentice} />
}

export const creationDateCellBody = (rowData: TrialPeriodReport) => {
  const createdOn = rowData.created_on

  if (!createdOn) {
    return ''
  }
  const dateString = getFormattedDateWithoutHours(createdOn)
  return <span className={'small-text'}>{dateString}</span>
}

export const meetingDateCellBody = (rowData: TrialPeriodReport) => {
  const discussionDate = rowData.discussion_date
  if (!discussionDate) {
    return ''
  }
  const dateString = getFormattedDateWithoutHours(discussionDate)
  return <span className={'small-text'}>{dateString}</span>
}

export const discussedCellBody = (rowData: TrialPeriodReport) => {
  let icon
  // 0 = true & discussed
  if (rowData.is_discussed !== undefined && rowData.is_discussed === 0) {
    icon = <i className="fas fa-check" />
  }

  return <span>{icon}</span>
}

export const getUnknownCell = (): JSX.Element => (
  <>
    <Avatar
      icon={'fas fa-question'}
      size={'normal'}
      shape="circle"
      className={'circle avatar'}
      imageAlt={''}
      onImageError={() => ''}
    />{' '}
    <span className={'person-name'}> {t<string>('core.unknown')}</span>
  </>
)

export const conclusionCellBody = (rowData: TrialPeriodReport) => {
  const statusClassName = classNames({
    '':
      rowData?.passed_status === Status.open || rowData?.passed_status === null,
    danger: rowData.passed_status === Status.failed,
    warning: rowData.passed_status === Status.extended,
    success: rowData.passed_status === Status.passed,
  })

  let statusAsText: string

  if (!rowData?.passed_status) {
    statusAsText = t<string>('core.document.status.open')
  } else {
    statusAsText = t<string>(
      'core.document.status.' + Status[rowData.passed_status]
    )
  }

  return (
    <Tag
      className={'tag'}
      value={statusAsText}
      severity={statusClassName as TagSeverityType}
    />
  )
}

export const TrialPeriodReportList = (): JSX.Element => {
  const toast = useRef<Toast>(null)

  const { loadedBasicInformation } = useAppSelector((state) => state.user)
  // the trial period reports from the store/backend
  const reports: TrialPeriodReport[] = useAppSelector(
    (state) => state.trialPeriodReports
  )
  const apprentices: Apprentice[] = useAppSelector(
    (state) => state.apprentices
  )
  const employees: Person[] = useAppSelector((state) => state.employees)

  // redux dispatcher
  const dispatch = useAppDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [selectedReport, setSelectedReport] = useState<
    TrialPeriodReport | undefined
  >(undefined)

  const [displayUploadDialog, setDisplayUploadDialog] =
    useState<boolean>(false)

  const [filterState, setFilterState] = useState({
    id_apprentice: { value: null, matchMode: FilterMatchMode.EQUALS },
    id_responsible: { value: null, matchMode: FilterMatchMode.EQUALS },
  })

  // changes visibility of delete dialog on delete button click or button click of buttons on the delete dialog itself
  const [isDeleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false)
  // const [responsible, setResponsible] = useState<number>(-1)

  /**
   * make sure that the reports are only requested from the api once
   */

  const fetchFromDB = async () => {
    if (reports.length === 0) {
      if (isLoading) {
        return
      }
      setIsLoading(true)
      const data = await loadAllTrialPeriodReports()

      const loadedReport = data?.data
      if (!loadedReport) {
        console.log('No reports were found.')
        setIsLoading(false)
        return
      }
      loadedReport.forEach((report: TrialPeriodReport) => {
        // hack for strict mode
        if (
          !reports.find(
            (e) => e.trial_period_report_id === report.trial_period_report_id
          )
        ) {
          dispatch(addTrialPeriodReport(report))
        }
      })
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!loadedBasicInformation) {
      return
    }
    fetchFromDB()
  }, [loadedBasicInformation]) // wait for people to be loaded

  const employeeCellBody = (rowData: TrialPeriodReport): JSX.Element => {
    if (rowData?.id_responsible === undefined) {
      return getUnknownCell()
    }
    const responsible = findPersonById(rowData.id_responsible, employees)
    if (!responsible) {
      return getUnknownCell()
    }
    return <AvatarWithName person={responsible} />
  }

  /**
   * open the signed report
   * @returns {Promise<void>}
   * @param rep
   */
  const openSignedFile = async (rep: TrialPeriodReport) => {
    if (!rep?.trial_period_report_id) {
      return
    }
    const downloadLinkResult = await handleRequest<SignedUrlResponse>(
      API_TRIAL_PERIOD_REPORT_DOWNLOAD(rep.trial_period_report_id),
      RequestMethod.GET
    )

    const signedUrl = downloadLinkResult?.data?.url
    if (signedUrl) {
      window.open(signedUrl)
      return
    }
    // todo show error toast
  }

  const statusCellBody = (rowData: TrialPeriodReport) => {
    if (rowData.signed_file_reference) {
      return (
        <span>
          {/*<span className="p-column-title">{t<string>('core.document.status.name')}</span>*/}
          <span className={'flex w-full'}>
            <ProgressBar
              color={'#ADCBBF'}
              value={100}
              showValue={true}
              className={'mr-1 w-full mt-2 min-width-120'}
            />
            <Button
              tooltip={t<string>('trial_period_reports.download_signed_report')}
              tooltipOptions={{ appendTo: document.body, position: 'left' }}
              key={UniqueKeyGenerator('signed_tpr')}
              type={'button'}
              icon="fas fa-file-pdf"
              className="p-button-rounded p-button-primary p-button-text mt-0 icon-green"
              onClick={() => openSignedFile(rowData)}
            />
          </span>
        </span>
      )
    }

    if (
      rowData.passed_status !== null &&
      rowData.passed_status !== Status.open
    ) {
      return (
        <span>
          <span className={'flex w-full'}>
            <ProgressBar
              color={'#7BAFDA'}
              value={75}
              showValue={true}
              className={'mr-1 w-full mt-2 min-width-120'}
            />
            <Button
              tooltip={t<string>('trial_period_reports.upload_signed_report')}
              tooltipOptions={{ appendTo: document.body, position: 'left' }}
              key={`uploadPdf_${rowData.trial_period_report_id}`}
              type={'button'}
              icon="fas fa-cloud-upload-alt "
              className="p-button-rounded p-button-secondary p-button-text mt-0"
              onClick={() => {
                setSelectedReport(rowData)
                setDisplayUploadDialog(true)
              }}
            />
          </span>
        </span>
      )
    }

    return (
      <span>
        <span className={'flex w-full'}>
          <ProgressBar
            color={'#7BAFDA'}
            value={25}
            showValue={true}
            className={'mr-1 w-full mt-2 mr-5 min-width-120'}
          />
          <span />
        </span>
      </span>
    )
  }

  const apprenticeOptionFilterTemplate = (id: number) => {
    let p = findApprenticeById(id, apprentices)
    if (!p) {
      return t<string>('core.all')
    }
    return <AvatarWithName person={p} />
  }

  const apprenticeFilterTemplate = (
    options: ColumnFilterElementTemplateOptions
  ): JSX.Element => {
    const filterValues: any = []
    apprentices.forEach((apprentice) => {
      if (apprentice.apprentice?.apprentice_id) {
        filterValues.push({
          label: apprentice.full_name,
          value: apprentice.apprentice.apprentice_id,
        })
      }
    })

    return (
      <Dropdown
        value={options.value}
        options={filterValues}
        valueTemplate={(_) => apprenticeOptionFilterTemplate(options.value)}
        itemTemplate={(e) => apprenticeOptionFilterTemplate(e.value)}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t<string>('core.all')}
        filter
        style={{
          height: 40,
          minHeight: 40,
          width: '100%',
          minWidth: '100%',
          maxWidth: 40,
        }}
        className="p-column-filter"
      />
    )
  }

  // close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogVisible(false)
  }

  // delete the selected entries by accepting the delete dialog popup
  const deleteSelectedEntries = async () => {
    const trialReportId = selectedReport?.trial_period_report_id
    if (trialReportId) {
      const result = await handleRequest(
        API_TRIAL_PERIOD_REPORT(trialReportId),
        RequestMethod.DELETE
      )
      if (!result.error) {
        // TODO success toast
        dispatch(removeTrialPeriodReport(trialReportId))
      } else {
        // TODO warn toast
        console.warn('Error occurred removing the trial period report.')
      }
    }
  }

  const addReport = () => {
    navigate(`${AuthRoutes.trialPeriodReport}/+`)
    return
  }

  const hideUploadDialog = () => {
    setDisplayUploadDialog(false)
    setSelectedReport(undefined)
  }

  const navigate = useNavigate()

  const edit = (row: TrialPeriodReport) => {
    if (row.trial_period_report_id) {
      navigate(`${AuthRoutes.trialPeriodReport}/${row.trial_period_report_id}`)
      return
    }
  }

  const deleteReport = (row: TrialPeriodReport) => {
    setSelectedReport(row)
    setDeleteDialogVisible(true)
  }

  /**
   * Get the upload url for the selected trial period report
   * @returns {string | undefined}
   */
  const getUploadUrl = () => {
    if (!selectedReport?.trial_period_report_id) {
      return undefined
    }
    return API_TRIAL_PERIOD_REPORT_UPLOAD(
      selectedReport?.trial_period_report_id
    )
  }

  const handleUpload = (id: number | undefined): void => {
    if (id === undefined) {
      return
    }
    if (id === -1) {
      const titleKey = t<string>('core.action.error.upload.file_type.title')
      const messageKey = t<string>(
        'core.action.error.upload.file_type.message'
      )
      showErrorToast(toast, titleKey, messageKey)
      return
    }
    const report = findTrialReportById(id, reports)
    if (report) {
      const updatedReport = { ...report }
      // todo, make pretty.. and set discussed to true
      updatedReport.signed_file_reference = 'created'
      dispatch(updateTrialPeriodReport(updatedReport))
      setDisplayUploadDialog(false)
      showCheckMarkToast(toast)
    }
  }

  const uploadValidationFailHandler = () => {
    // TODO user output
    console.warn('The file is not valid to be uploaded.')
  }

  const fileUploadProps = {
    handleUploadResult: handleUpload,
    uploadUrl: getUploadUrl(),
    documentId: selectedReport?.trial_period_report_id,
  }

  const [showAll, setShowAll] = useState<boolean>(false)
  const [displayedReports, setDisplayedReports] = useState<TrialPeriodReport[]>(
    []
  )

  useEffect(() => {
    if (showAll) {
      setDisplayedReports(reports)
    } else {
      // feel free to refactor whoever stumbles over this piece of code...
      const reportsFromActiveApprentices: TrialPeriodReport[] = []
      apprentices
        .filter((a) => a.is_active === 1 && a.apprentice !== undefined)
        .map((x) => {
          if (x.apprentice?.apprentice_id) {
            const reportsFromActiveApprentice = reports.filter(
              (r) => r.id_apprentice === x.apprentice?.apprentice_id
            )
            reportsFromActiveApprentice.forEach((r) =>
              reportsFromActiveApprentices.push(r)
            )
          }
        })
      setDisplayedReports(reportsFromActiveApprentices)
    }
  }, [reports, showAll])

  return (
    <div className={'card pr-2'}>
      <div
        className={
          'inline-flex mb-2 w-full justify-content-between align-items-center'
        }
      >
        <div>
          <Button
            label={t<string>('trial_period_reports.add')}
            className={'p-button p-button-primary'}
            onClick={() => addReport()}
            icon={'fa-solid fa-plus'}
            type="button"
          />
        </div>
        <div>
          <Dropdown
            value={showAll}
            className={'min-width-240'}
            options={[
              { value: true, label: t<string>('core.show_all') },
              {
                value: false,
                label: t<string>('core.show_active'),
              },
            ]}
            onChange={(e) => setShowAll(!showAll)}
          />
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <DataTable
            className={'datatable-reports p-datatable'}
            value={displayedReports}
            scrollHeight={'calc(100vh - 190px)'}
            onSelectionChange={(e) => setSelectedReport(e.value)}
            dataKey="trial_period_report_id"
            emptyMessage={t<string>('core.empty_message')}
            resizableColumns
            removableSort
            groupRowsBy={'id_apprentice'}
            rowGroupMode={'rowspan'}
            sortMode={'single'}
            sortField={'id_apprentice'}
            columnResizeMode="fit"
            rowHover
            stripedRows
            rowClassName={(r) =>
              InactiveRowStyleByObjectAndPerson(r, r.id_apprentice, apprentices)
            }
            filters={filterState}
            filterDisplay={'row'}
          >
            <Column
              field="id_apprentice"
              header={t<string>('core.apprentice.neutral')}
              body={(e) => apprenticeCellBody(e, apprentices)}
              filter
              filterElement={(e) => apprenticeFilterTemplate(e)}
              filterPlaceholder={t<string>('core.all')}
              showFilterMenu={false}
            />
            <Column
              field="passed_status"
              header={t<string>('trial_period_reports.conclusion')}
              body={conclusionCellBody}
              sortable
            />
            <Column
              field="id_responsible"
              header={t<string>('core.responsible')}
              body={employeeCellBody}
              filter
              filterElement={(e) => EmployeeDataTableFilter(e, employees)}
              filterPlaceholder={t<string>('core.all')}
              showFilterMenu={false}
            />
            <Column
              field="created_on"
              header={t<string>('core.created_on')}
              sortable
              body={creationDateCellBody}
              dataType={'date'}
            />
            <Column
              field="discussion_date"
              header={t<string>('core.document.discussion')}
              sortable
              body={meetingDateCellBody}
              dataType={'date'}
            />
            <Column
              field="is_discussed"
              header={t<string>('core.document.discussed')}
              body={discussedCellBody}
              sortable
            />
            <Column
              field="signed_file_reference"
              header={t<string>('core.document.status.name')}
              body={statusCellBody}
            />
            <Column
              // header={t<string>('content.table.header.actions')}
              body={actionBodyWithEditAndDelete(edit, deleteReport)}
              style={{ width: '2em' }}
            />
          </DataTable>
        )}
      </div>
      <DeleteConfirmationDialog
        onClose={() => closeDeleteDialog()}
        onDelete={() => deleteSelectedEntries()}
        acceptLabelKey={'core.delete'}
        rejectLabelKey={'core.cancel'}
        visible={isDeleteDialogVisible}
        dataToDelete={selectedReport}
        title={t<string>('title.trial_period_report')}
        message={t<string>('dialog.delete.question_single_delete')}
      />
      <Dialog
        onHide={hideUploadDialog}
        id={'upload_dialog'}
        header={`${t<string>('title.trial_period_report')} ${t<string>(
          'core.upload'
        )} `}
        visible={displayUploadDialog}
      >
        <MokaiUpload
          maxFileSize={MAX_FILE_SIZE}
          showUploadButton={true}
          allowMultiple={false}
          allowedTypes={'application/pdf'}
          uploadHandler={handleFileUpload(fileUploadProps)}
          failHandler={uploadValidationFailHandler}
        />
      </Dialog>
      <Toast ref={toast} position={'bottom-right'} />
    </div>
  )
}

export default TrialPeriodReportList